<template>
  <div class="custom-border-outline mt-3">
    <div class="d-flex justify-content-between flex-wrap align-items-center">
      <h5 class="text-evollu-gray-light is-bold mb-0 pl-3 py-3">
        {{$t('iframe.titles.project_structure')}}
      </h5>

      <i
        v-if="collapsable"
        class="clickable p-3"
        :class="{'fa fa-chevron-up': !isCollapsed, 'fa fa-chevron-down': isCollapsed}"
        @click="isCollapsed = !isCollapsed"
      >
      </i>
    </div>
    <div class="ml-3 mr-3"v-if="!isCollapsed">
      <!--Header-->
      <div class="row col-12">
        <div class="row col-md-2">
          <label class="header-lbl">{{$t('iframe.pages_configurator.headings.order')}}</label>
        </div>
        <div class="row col-md-4">
          <label class="header-lbl">{{$t('iframe.pages_configurator.headings.type')}}</label>
        </div>
        <div class="row col-md-4">
          <label class="header-lbl">{{$t('iframe.pages_configurator.headings.name')}}</label>
        </div>
        <div class="row col-md-2 w-100 d-flex justify-content-center">
          <label class="header-lbl">{{$t('iframe.pages_configurator.headings.actions')}}</label>
        </div>
      </div>
      <!--End Header-->

      <!--Body-->
      <div class="col-12" id="pagesContainer">
        <!-- Pages Area -->
        <div class="row col-12 custom-border-outline justify-content-center align-content-center" style="height: 60px" v-if="addedPages.length===0">
          <label class="placeholder-label">{{$t('iframe.pages_configurator.no_pages_added')}}</label>
        </div>
        <div v-else>
          <div class="row col-12 mt-2 custom-border-outline align-content-center" style="height: 80px" v-for="(page, idx) of addedPages">
            <div class="row col-md-2 d-flex flex-column">
              <i class="fa fa-caret-up" v-bind:class="{'clickable': idx !== 0, 'sort-icon-disabled': idx === 0}" @click="idx !== 0 ? handleSort('up', page) : null"/>
              <i class="fa fa-caret-down" v-bind:class="{'clickable': idx !== addedPages.length-1, 'sort-icon-disabled': idx === addedPages.length-1}" @click=" idx !== addedPages.length-1 ? handleSort('down', page) : null"/>
            </div>
            <div class="row custom-multiselect col-md-4">
              <multiselect
                :options="pageTypes"
                id="pageTypeMultiselect"
                v-model="page.type"
                :custom-label="val => $t(`iframe.page_types.${val.type}`)"
                track-by="id"
                select-label=""
                selected-label=""
                deselect-label=""
                placeholder="---"
                open-direction="bottom"
                :allow-empty="false"
                :multiple="false"
                :searchable="true"
                @input="handleInputPageType(page)"
              >
                <span slot="noResult">{{ $t('patients.noResults') }}</span>
              </multiselect>
            </div>
            <div class="row custom-multiselect col-md-4">
              <multiselect
                :disabled="!page.type"
                :options="filteredPages"
                id="pageNameMultiselect"
                v-model="page.page"
                :custom-label="(val) => val.name !== '' ? val.name : 'N/A'"
                track-by="id"
                select-label=""
                selected-label=""
                deselect-label=""
                placeholder="---"
                open-direction="bottom"
                :allow-empty="false"
                :multiple="false"
                :searchable="true"
                @open="handlePageOptions(page)"
              >
                <span slot="noResult">{{ $t('patients.noResults') }}</span>
              </multiselect>
            </div>
            <div class="row col-md-2 w-100 d-flex justify-content-center">
              <va-popover :message="$t('iframe.actions.delete')" placement="top">
                <i class="report-action-icon fa fa-trash-o p-2 clickable" @click.prevent.stop="removePage(page)"/>
              </va-popover>
            </div>
          </div>
        </div>
        <!---------------->

        <!-- Add new pages -->
        <div class="row col-12 mt-3 mb-4">
          <div class="row col-12 mb-2">
            <label>{{$t('iframe.pages_configurator.add_new_block')}}</label>
          </div>
          <div class="row col-md-2">
            ---
          </div>
          <div class="row custom-multiselect col-md-4">
            <multiselect
              :options="pageTypes"
              id="addPageTypeMultiselect"
              v-model="pageToAdd.type"
              :custom-label="val => $t(`iframe.page_types.${val.type}`)"
              track-by="id"
              select-label=""
              selected-label=""
              deselect-label=""
              placeholder="---"
              open-direction="bottom"
              :allow-empty="false"
              :multiple="false"
              :searchable="true"
            >
              <span slot="noResult">{{ $t('patients.noResults') }}</span>
            </multiselect>
          </div>
          <div class="row custom-multiselect col-md-4">
            <multiselect
              :disabled="!pageToAdd.type"
              :options="filteredPages"
              id="addPageNameMultiselect"
              v-model="pageToAdd.page"
              :custom-label="(val) => val.name !== '' ? val.name : 'N/A'"
              track-by="id"
              select-label=""
              selected-label=""
              deselect-label=""
              placeholder="---"
              open-direction="bottom"
              :allow-empty="false"
              :multiple="false"
              :searchable="true"
            >
              <span slot="noResult">{{ $t('patients.noResults') }}</span>
            </multiselect>
          </div>
          <div class="row col-md-2 w-100 d-flex justify-content-center">
            ---
          </div>
        </div>
      </div>
      <!--End Body-->

    </div>
  </div>
</template>

<script>
import Multiselect from 'vue-multiselect'
import iframeModule from '@/store/modules/iframe'
import {mapState} from "vuex";
export default {
  components: {
    Multiselect
  },
  props: {
    value: {
      type: Array,
      required: false
    },
    collapsable: {
      type: Boolean,
      default: false
    },
    collapsed: {
      type: Boolean,
      default: false
    },
    client: {
      type: Object,
      required: true,
      default: {}
    },
    pages: {
      type: Array,
      default: []
    }
  },
  data: function() {
    return {
      isCollapsed: this.collapsable && this.collapsed,
      pageToAdd: {
        type: null,
        page: null,
      },
      addedPages: [],
      filteredPages: []
    }
  },
  mounted () {
    for(const[index, page] of this.value.entries()) {

      this.addedPages.push({
        order: index,
        type: this.pageTypes.filter(elem => {
          if (page.type === "hearing_test_page") {
            if (page.hearing_test_type === "tonal") {
              return elem.type === "tonal_test_page"
            } else if (page.hearing_test_type === "noise") {
              return elem.type === "noise_test_page"
            }
          }
          return elem.type === page.type
        })[0],
        page: page
      })
    }
  },
  methods: {
    handleInputPageType(page) {
      this.handlePageOptions(page)
      page.page = null
    },
    handlePageOptions(page)  {
      const addedPages = this.addedPages.filter(elem => elem.page !== null)
      const addedPagesIds = addedPages.map(elem => elem.page.id)
      if(page.type.type === 'tonal_test_page') {
        this.filteredPages = this.pages.filter(elem => elem.type === 'hearing_test_page'
          && elem.hearing_test_type === 'tonal'
          && !addedPagesIds.includes(elem.id))
      }
      else if(page.type.type === 'noise_test_page') {
        this.filteredPages = this.pages.filter(elem => elem.type === 'hearing_test_page'
          && elem.hearing_test_type === 'noise'
          && !addedPagesIds.includes(elem.id))
      } else {
        this.filteredPages = this.pages.filter(elem => elem.type === page.type.type
          && !addedPagesIds.includes(elem.id))
      }

    },
    removePage: function(page) {
      this.addedPages.splice(this.addedPages.findIndex(item => item.id === page.id && item.order === page.order), 1)
      this.pageToAdd = {
        type: null,
        page: null,
      }
    },
    handleSort: function(direction, page) {
      const currentElemIdx = this.addedPages.findIndex(item => item.id === page.id && item.order === page.order)
      const currentElemOrderTemp = page.order

      if(direction === 'down') {
        const nextElemOrderTemp = this.addedPages[currentElemIdx+1].order
        //update the current element to have the orderNr of the next one
        this.addedPages[currentElemIdx].order = nextElemOrderTemp
        //update the next element to have the orderNr of the current one
        this.addedPages[currentElemIdx+1].order = currentElemOrderTemp

        //sort the array by order
        this.addedPages = this.addedPages.sort((a, b) => (a.order > b.order) ? 1 : -1)
      } else if (direction === 'up') {
        const lastElemOrderTemp = this.addedPages[currentElemIdx-1].order
        //update the current element to have the orderNr of the next one
        this.addedPages[currentElemIdx].order = lastElemOrderTemp
        //update the next element to have the orderNr of the current one
        this.addedPages[currentElemIdx-1].order = currentElemOrderTemp

        //sort the array by order
        this.addedPages = this.addedPages.sort((a, b) => (a.order > b.order) ? 1 : -1)
      }
    },
    filterPages: function(val) {
      const addedPagesIds = this.addedPages.map(elem => elem.page.id)
      if(val.type === 'noise_test_page') {
        this.filteredPages = this.pages.filter(page => page.type === 'hearing_test_page'
          && page.hearing_test_type !== undefined
          && page.hearing_test_type === 'noise'
          && !addedPagesIds.includes(page.id)
          && page.client_id === this.client.id)

      }
      else if(val.type === 'tonal_test_page') {
        this.filteredPages = this.pages.filter(page => page.type === 'hearing_test_page'
          && page.hearing_test_type !== undefined
          && page.hearing_test_type === 'tonal'
          && !addedPagesIds.includes(page.id)
          && page.client_id === this.client.id)
      } else {
        this.filteredPages = this.pages.filter(page => page.type === val.type && !addedPagesIds.includes(page.id) && page.client_id === this.client.id)
      }
    }
  },
  computed: {
    ...mapState({
      'pageTypes': (state) => state.iframe.pageTypes,
    }),
  },
  watch: {
    'pageToAdd.type': {
      handler: function(val) {
        if(val) {
          this.filterPages(val)
        }
      },
    },
    'pageToAdd.page': {
      handler: function(val) {
        if(val && this.pageToAdd.type !== null) {
          let lastIdx = this.addedPages.length

          let type = null
          if(val.type === "hearing_test_page"){
            if(val.hearing_test_type !== undefined && val.hearing_test_type === 'noise') {
              type='noise_test_page'
            } else if(val.hearing_test_type !== undefined && val.hearing_test_type === 'tonal') {
              type='tonal_test_page'
            }
          }else {
            type = val.type
          }

          this.addedPages.push({
            order: lastIdx,
            type: this.pageTypes.filter(elem => elem.type === type)[0],
            page: val
          })
          this.pageToAdd = {
            type: null,
            page: null,
          }

        }
      }
    },
    client: function() {
      this.addedPages = []
    }
  }
}
</script>

<style scoped>
.custom-border-outline {
  border: 2px solid rgba(65, 65, 65, 0.1);
  border-radius: 25px;
}

.custom-multiselect {
  padding-left: 0 !important;
  padding-right: 45px !important;
}

.placeholder-label {
  color:  rgba(194, 194, 194, 0.55)
}
.sort-icon-disabled {
  opacity: 0.2;
}
</style>
<style lang="scss">
@import "~vuestic-ui/src/components/vuestic-sass/vuestic-styles";

.header-lbl{
  padding-left: 5px;
  color: $evollu-dark;
}
</style>
